//lib
import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Typography } from "@mui/material";
//src
import CreateProjectModalPropType from "./create-project-modal.type";
import { CreateProjectModalStyled } from "./create-widget-modal.styles";
import ProjectInfoForm, {
  Project_INFO_FORM_VIEW_TYPES,
} from "../project-details/basic-info/project-info-form/project-info-form";
import { CreateProjectModalValidations } from "../../../constants/validations";
import {
  useGetClientsList,
  useGetProjectsUsersList,
} from "../../../utils/projects-list.utils";
import { useAuth } from "../../../context/AuthContext";
import useProjectGroups from "../../../hooks/useProjectGroups";
import { getNextProjectId } from "../../../services/countersService";
import {
  projectsCollection,
  projectGroupCollection,
} from "../../../services/firebase";
import useContacts from "../../../hooks/useContacts";
import Loader from "../../../components/loader/loader";

const CreateProjectModal = (props) => {
  const {
    onClose,
    nextId,
    defaultValues = {},
    isCreationGroupView = false,
  } = props;
  const { userDetails, isLoading } = useAuth();
  const [clientsList, setCLientsList] = useState([]);
  const { leadsList } = useGetProjectsUsersList();
  const [isClientsLoading, setIsClientsLoading] = useState(true);

  const [redirectPath, setRedirectPath] = useState(null);

  const { data: projectGroupOptions, isLoading: isLoadingProjectGroups } =
    useProjectGroups(true);

  const { contacts, isLoading: isLoadingContacts } = useContacts();
  useGetClientsList((clients) => {
    setIsClientsLoading(false);
    setCLientsList(clients);
  });
  const isDataLoading =
    isLoading ||
    isLoadingContacts ||
    isLoadingProjectGroups ||
    isClientsLoading;

  const form = useForm({
    mode: "all",

    defaultValues: {
      id: nextId?.toString(),
      projectName: "",
      isGrouped: false,
      isNonBillable: false,
      state: "",
      addToExistingProject: "",
      source: "",
      client: "",
      serviceValue: "",
      projectLead: "",
      start: "",
      teamList: [],
      probability: "",
      serviceGroups: [],
      schedule: [{ value: "", month: 1 }],
      projectDescription: "",
      dateCreated: "",
      createdBy: "",
      ...defaultValues,
    },
    resolver: yupResolver(CreateProjectModalValidations(isCreationGroupView)),
  });

  const { watch, formState, getValues } = form;
  const { isValid } = formState;
  watch();

  const selectedClient = watch().client;

  const contactList = watch().client
    ? contacts
        ?.filter(
          (contact) =>
            contact.companyID === selectedClient ||
            contact.companyName === selectedClient
        )
        ?.map((contact) => ({
          label: `${contact?.firstName} ${contact?.lastName}`,
          value: contact?.id,
        }))
    : [];

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      id: nextId ? nextId.toString() : "", // Convert to string only here for form handling
    });
  }, [nextId, form]);

  // Dynamically determine the view type
  const viewType = isCreationGroupView
    ? Project_INFO_FORM_VIEW_TYPES.creationGroup
    : Project_INFO_FORM_VIEW_TYPES.creation;

  const handleSaveModal = async (formData) => {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}/${
      currentDate.getMonth() + 1
    }/${currentDate.getFullYear()}`;
    formData.createdBy = userDetails?.fullName;
    formData.dateCreated = formattedDate;

    let formattedStartDate;
    try {
      const localDate = new Date(formData.start);
      const year = localDate.getFullYear();
      const month = (localDate.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed
      const day = localDate.getDate().toString().padStart(2, "0");
      formattedStartDate = `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Invalid start date:", formData.start);
      formattedStartDate = ""; // Handle as needed
    }

    try {
      const projectId = await getNextProjectId(); // Fetch the next project ID atomically
      const projectData = {
        id: projectId, // Use numeric ID
        isNew: true,
        projectName: formData.projectName,
        isGrouped: isCreationGroupView,
        isNonBillable: formData.isNonBillable,
        state: formData.state,
        addToExistingProject: formData.addToExistingProject,
        source: formData.source,
        client: formData.client,
        contact: formData.contact,
        serviceValue: formData.serviceValue,
        projectLead: formData.projectLead,
        start: formattedStartDate, // Store adjusted date as ISO string
        teamList: formData.teamList,
        probability: formData.probability,
        serviceGroups: formData.serviceGroups,
        schedule: formData.schedule.map((item) => ({
          value: item.value || "0.00",
          month: item.month,
        })),
        projectDescription: formData.projectDescription,
        createdBy: formData.createdBy,
        dateCreated: formattedDate,
      };

      if (formData.isGrouped) {
        // Save to projectGroupCollection
        const groupRef = projectGroupCollection.doc(projectId.toString());
        await groupRef.set(projectData);
        console.log("Grouped project saved successfully:", projectData);
      } else {
        // Save to projectsCollection
        const projectRef = projectsCollection.doc(projectId.toString());
        await projectRef.set(projectData);
        console.log("Project saved successfully:", projectData);
      }

      console.log("Project saved successfully:", projectData);
      onSuccess(projectData);
      console.log("Project saved successfully:1", projectData);
    } catch (error) {
      console.error("Error saving project:", error);
    }
  };

  const handleSubmit = () => {
    const formData = getValues();
    handleSaveModal(formData);
  };

  const onSuccess = (projectData) => {
    // Setup redirect path based on whether the project is grouped or not
    const search = projectData.isGrouped
      ? "?isGroupView=true"
      : "?isGroupView=false";
    setRedirectPath(`/projectsview/${projectData.id}/details${search}`);
    onClose(); // Close the modal after setting the redirect
  };

  if (redirectPath) {
    // Use Navigate for redirection if path is set
    return <Navigate to={redirectPath} />;
  }

  return (
    <CreateProjectModalStyled
      confirmBtnTitle={
        isCreationGroupView ? "Create Project Group" : "Create Project"
      }
      confirmBtnColor="#77E250"
      closeBtnTitle={"Cancel"}
      isConfirming={isLoading}
      disableConfirmBtn={!isValid}
      onConfirm={handleSubmit}
      onClose={onClose}
      isGrouped={getValues()?.isGrouped}
    >
      <div>
        <div className="head">
          <Typography variant="h4">
            {isCreationGroupView ? "Create Project Group" : "Create Project"}
          </Typography>
        </div>
        {isDataLoading ? (
          <div style={{ height: "600px" }}>
            <Loader />
          </div>
        ) : (
          <ProjectInfoForm
            form={form}
            view={viewType}
            clientOptions={clientsList}
            clientContactOptions={contactList}
            projectLeadOptions={leadsList}
            projectGroupOptions={projectGroupOptions}
          />
        )}
      </div>
    </CreateProjectModalStyled>
  );
};

CreateProjectModal.propTypes = CreateProjectModalPropType;

export default CreateProjectModal;
